import { CustomButton, ResponsiveDialog } from "@hapinest/theme";
import { Box, Link, makeStyles, Step, StepContent, StepLabel, Stepper, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { isNotProduction, useI18N } from "../../../common/common";
import { useBreakpoints, useHelpSection, useKeycloakFunctions, usePdfDocumentsBySlug, useUserAccounts } from "../../../hooks";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useAnalytics } from "../../../store/providers/AnalyticsProvider";
import { HELP_CENTER } from "../../../common/constant";
import { withSnackbar } from "../../hoc/SnackbarHOC";
import { checkNewPdfContractDisabled, checkOpportunityFlowADisabled } from "../../../common/configcat/functions";
import { LocaleContext } from "../../../store/providers/LocaleProvider";
import { LANGUAGE } from "../../../common/i18n/locales/localeConstant";

const useStyles = makeStyles((theme) => ({
    dialogRoot: {
        "& .MuiDialogContent-root": {
            padding: theme.spacing(0, 5),
            [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0, 3)
            },
        },
        "& .MuiDialogActions-root": {
            flexDirection: "column",
            gap: `${theme.spacing(2)}px`,
            marginBottom: theme.spacing(5),
            [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0, 3)
            },
            "& button": {
                margin: 0,
                width: "100%",
                [theme.breakpoints.up("sm")]: {
                    maxWidth: "220px"
                },
            }
        }
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    stepperRoot: {
        padding: theme.spacing(5, 0, 0),
        "& .MuiStep-completed": {
            "& + .MuiStepConnector-root .MuiStepConnector-line, & .MuiStepContent-root": {
                borderColor: theme.palette.primary[700]
            }
        },
        "& .MuiStepLabel-root": {
            alignItems: "flex-start",
            "& .MuiStepLabel-iconContainer": {
                paddingRight: theme.spacing(2)
            },
        },
        "& .MuiStepIcon-text": {
            fontSize: theme.spacing(2),
            fontWeight: 600,
            transform: `translateY(${theme.spacing(0.15)}px)`,
            fill: theme.palette.text.white,
        },
        "& .MuiStepIcon-root": {
            color: theme.palette.grey[500],
            "&.MuiStepIcon-completed, &.MuiStepIcon-active": {
                color: theme.palette.green,
            }
        },
        "& .MuiStepConnector-root": {
            padding: 0,
            "& .MuiStepConnector-line": {
                borderColor: theme.palette.borders.line.light
            }
        },
        "& .MuiStepContent-root": {
            marginTop: 0,
            borderLeft: `1px solid ${theme.palette.borders.line.light}`,
            paddingLeft: theme.spacing(3.5),
            "&.MuiStepContent-last": {
                borderLeft: "none"
            }
        }
    },
    bold: {
        fontWeight: "bold"
    },
    contentLink: {
        textDecoration: "underline",
        fontWeight: 500,
        cursor: "pointer"
    },
    link: {
        fontWeight: 600
    }
}));

const AB_TEST_DATA = {
    abtest_id: "GN_isDirectSalesDisabled_20240809",
    abtest_name: "Show new direct sales flow",
    abtest_variation_id: "variant1",
    abtest_variation_name: "Variant 1"
};

const DownloadContractStepperDialog = ({ isOpen, onClose, showSnackbar }) => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [documentTab, setDocumentTab] = useState(undefined);
    const [isPdfContractDisabled, setIsPdfContractDisabled] = useState(false);
    const [isOpportunityFlowADisabled, setOpportunityFlowADisabled] = useState(false);

    const { i18n } = useI18N();
    const { isSmallScreen, isExtraSmallScreen } = useBreakpoints();
    const { sendCustomEvent, eventNames } = useAnalytics();
    const { getUserDirectSalesContract, directSalesContract, loadingDirectSalesContract, errorDirectSalesContract, setLoadingDirectSalesContract } = useUserAccounts();
    const {initialized, userId} = useKeycloakFunctions();
    const {pdfDocuments, getPdfDocuments} = usePdfDocumentsBySlug();
    const {localeUppercase, locale, localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const {getHelpUrl} = useHelpSection();

    const pdfContractUrl = useMemo(() => {
        if (!isPdfContractDisabled && directSalesContract) {
            return window.URL.createObjectURL(directSalesContract);
        } else if (isPdfContractDisabled && pdfDocuments) {
            return pdfDocuments[0]?.pdfDocumentCollection?.items?.[0]?.url;
        } else {
            return "";
        }
    }, [isPdfContractDisabled, directSalesContract, pdfDocuments]);

    useEffect(() => {
        if (isOpen) {
            if (sendCustomEvent) {
                sendCustomEvent({
                    event_name: eventNames.DIRECT_SALES_BUYNOW_BTN
                });
                sendCustomEvent({
                    event_name: eventNames.DIRECT_SALES_PROMPT_VIEW,
                    ...AB_TEST_DATA
                });
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (pdfContractUrl && documentTab) {
            printPdf();
        }
    }, [pdfContractUrl, documentTab]);

    useEffect(() => {
        if (initialized) {
            checkNewPdfContractDisabled(userId).then((res) => {
                setIsPdfContractDisabled(res);
            });

            checkOpportunityFlowADisabled(userId).then((res) => {
                setOpportunityFlowADisabled(res);
            });
        }
    }, [initialized]);

    useEffect(() => {
        if (errorDirectSalesContract) {
            showSnackbar({
                type: "warning",
                message: i18n("common.label.generic_error"),
                withIcon: true,
                closeIcon: true
            });
        }
    }, [errorDirectSalesContract]);

    const handleCloseDialog = () => {
        triggerClosePopupEvent();
        onClose();
    };

    const handleActionButtonClick = () => {
        if (activeStep === 0) {
            handleGetPdfDocuments();
            if (sendCustomEvent) {
                sendCustomEvent({
                    event_name: eventNames.DIRECT_SALES_OPENCONTRACT_POPUP
                });
            }
        } else {
            triggerClosePopupEvent();
            onClose();
        }
    };

    const triggerClosePopupEvent = () => {
        if (sendCustomEvent) {
            sendCustomEvent({
                event_name: eventNames.DIRECT_SALES_CLOSE_POPUP
            });
        }
    };

    const handleAdviceButtonClick = () => {
        if (sendCustomEvent) {
            sendCustomEvent({
                event_name: eventNames.DIRECT_SALES_ADVICE_POPUP
            });
        }
        const isInMarketplace = window.location.href.includes("/marketplace");
        window.open(getHelpUrl(HELP_CENTER.section.CONTACT_REQUEST, isInMarketplace ? HELP_CENTER.source.MARKETPLACE_CONTACT : HELP_CENTER.source.NCE_WEBSITE), "_blank");
    };

    const handleGetPdfDocuments = () => {
        if (!isPdfContractDisabled) {
            getUserDirectSalesContract("A", null, null, !isOpportunityFlowADisabled);
            setDocumentTab(window.open("", "_blank"));
        } else {
            setLoadingDirectSalesContract(true);
            getPdfDocuments({variables: {slugs: ["contract"], locale: localeUppercase}});
            setDocumentTab(window.open("", "_blank"));
        }
    };

    const handleDownloadContract = () => {
        setDocumentTab(window.open("", "_blank"));
        setLoadingDirectSalesContract(true);
    };

    const printPdf = () => {
        setActiveStep(1);
        documentTab.location.href = pdfContractUrl;
        setLoadingDirectSalesContract(false);
    };

    const getHyperLink = (str) => {
        return (
            <Link
                className={classes.link}
                key={str}
                href={`${isNotProduction() ? "https://hapimagag--uat.sandbox.my.site.com/s/contract?language=" : "https://help.hapimag.com/s/contract?language="}${(locale === LANGUAGE.EN || locale === LANGUAGE.NL) ? localeUppercaseUnderscore : locale?.split("-")[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
            >
                {str}
            </Link>
        );
    };

    const steps = [
        {
            id: 1,
            label: i18n("view.marketplace.share_banner.dialog.save_contract_form"),
            content: i18n("view.marketplace.share_banner.dialog.save_it"),
            contentLink: i18n("view.marketplace.share_banner.dialog.download_contract_again")
        },
        {
            id: 2,
            label: i18n("view.marketplace.share_banner.dialog.sign_and_upload"),
            content: i18n("view.marketplace.share_banner.dialog.fill_and_upload", { hyperlink: getHyperLink })
        },
        {
            id: 3,
            label: i18n("view.marketplace.share_banner.dialog.payment_and_bookings"),
            content: i18n("view.marketplace.share_banner.dialog.notify")
        }
    ];

    return (
        <ResponsiveDialog
            open={isOpen}
            handleClose={handleCloseDialog}
            hasCloseButton
            className={classes.dialogRoot}
            content={(
                <>
                    <Typography
                        variant={`${isExtraSmallScreen() || isSmallScreen() ? "h4" : "h3"}`}
                        align="center"
                        className={classes.mb2}
                    >
                        {i18n("view.buy_a_share.title")}
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                    >
                        {i18n("view.marketplace.share_banner.dialog.follow_steps")}
                    </Typography>
                    <Stepper
                        orientation="vertical"
                        activeStep={activeStep}
                        className={classes.stepperRoot}
                    >
                        {steps.map((s) => (
                            <Step
                                key={`step--${s.id}`}
                                expanded
                            >
                                <StepLabel>
                                    <Typography
                                        variant={activeStep > (s.id - 2) ? "subtitle2" : "body2"}
                                        className={clsx({ [classes.bold]: activeStep > (s.id - 2) })}
                                    >
                                        {s.label}
                                    </Typography>
                                </StepLabel>
                                {s.content &&
                                    <StepContent>
                                        <Typography variant="body1">
                                            {s.content}
                                        </Typography>
                                        {(s.id === 1 && activeStep > 0) &&
                                            <Typography
                                                variant="body1"
                                                onClick={handleDownloadContract}
                                                className={clsx({ [classes.contentLink]: s.id === 1 })}
                                            >
                                                {s.contentLink}
                                            </Typography>
                                        }
                                    </StepContent>
                                }
                            </Step>
                        ))}
                    </Stepper>
                </>
            )}
            actions={(
                <Box
                    display="flex"
                    flexDirection={"row-reverse"}
                    justifyContent="center"
                    alignItems="center"
                    gridGap={16}
                    width={"100%"}
                    flexWrap={"wrap"}
                >
                    <CustomButton
                        variant="contained"
                        onClick={handleActionButtonClick}
                        size={"medium"}
                        loading={loadingDirectSalesContract}
                    >
                        {activeStep > 0 ? i18n("common.label.ok") : i18n("view.marketplace.share_banner.dialog.download_contract")}
                    </CustomButton>
                    <CustomButton
                        variant="outlined"
                        color="black"
                        onClick={handleAdviceButtonClick}
                        size={"medium"}
                    >
                        {i18n("view.marketplace.share_banner.personalised_advice")}
                    </CustomButton>
                </Box>
            )}
        />
    );
};

DownloadContractStepperDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    showSnackbar: PropTypes.func
};

export default withSnackbar(DownloadContractStepperDialog);
